import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { apiErrorMessage, lawyerAuth, lawyerService } from "../../services/lawyerApi";
import "../../components/lawyer/lawyer.css";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

const LawyerVerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || lawyerAuth.getPendingEmail();

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const inputs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  if (!email) return <Navigate to="/lawyer/login" replace />;

  const focusAt = (i) => inputs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))]?.focus();

  const fillFrom = (index, value) => {
    const chars = value.replace(/\D/g, "").slice(0, OTP_LENGTH - index).split("");
    if (!chars.length) return;
    const next = [...digits];
    chars.forEach((c, k) => {
      next[index + k] = c;
    });
    setDigits(next);
    focusAt(index + chars.length);
  };

  const handleChange = (index, value) => {
    if (value === "") {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    fillFrom(index, value);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      focusAt(index - 1);
    } else if (e.key === "ArrowLeft") {
      focusAt(index - 1);
    } else if (e.key === "ArrowRight") {
      focusAt(index + 1);
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    fillFrom(index, e.clipboardData.getData("text"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length !== OTP_LENGTH) {
      toast.error("Please enter the 4-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await lawyerService.verifyOTP(email, otp);
      const data = res?.data?.data;
      if (res?.data?.status && data?.auth_token) {
        lawyerAuth.setSession(data.auth_token, data.user);
        lawyerAuth.clearPendingEmail();
        toast.success(res.data.message || "Verified successfully");
        navigate("/lawyer/dashboard", { replace: true });
      } else {
        toast.error(res?.data?.message || "Invalid OTP.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const res = await lawyerService.resendOTP(email);
      if (res?.data?.status) {
        toast.success(res.data.message || "OTP sent again");
        setDigits(Array(OTP_LENGTH).fill(""));
        setCooldown(RESEND_SECONDS);
        focusAt(0);
      } else {
        toast.error(res?.data?.message || "Unable to resend OTP.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="lwp lwp-auth">
      <Seo title="Verify Code | Legal Platform" noindex />
      <div className="lwp-auth-card lwp-otp-card">
        <h1 className="lwp-otp-title">Verification code</h1>
        <p className="lwp-otp-sub">We have sent the code verification to your email</p>
        <p className="lwp-otp-email">
          <span>{email}</span>
          <button
            type="button"
            className="lwp-icon-btn"
            aria-label="Change email"
            onClick={() => navigate("/lawyer/login", { state: { email } })}
          >
            <i className="bi bi-pencil" />
          </button>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="lwp-otp-boxes">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                className="lwp-otp-box"
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={OTP_LENGTH}
                value={d}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={(e) => handlePaste(i, e)}
                onFocus={(e) => e.target.select()}
              />
            ))}
          </div>

          <button type="submit" className="lwp-btn" disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : "Submit"}
          </button>
        </form>

        <button
          type="button"
          className="lwp-resend"
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
        >
          {resending ? "Sending..." : cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
        </button>
      </div>
    </div>
  );
};

export default LawyerVerifyOtp;
